import { FloatingFormItem, Group, InputDecimal } from '@/components/_common';
import {
  Button,
  Avatar,
  Checkbox,
  Col,
  Collapse,
  Empty,
  Form,
  Input,
  Row,
  Space,
  Table,
  TableColumnType,
  Tag,
  Tooltip,
  Typography,
} from 'antd';
import { Check, Filter, Info, Package, Search, X } from 'react-feather';
import { useCallback, useMemo, useState } from 'react';
import formatterPrice from '@/utils/number/formatterPrice';
import { decimalToTime } from '@/utils/functions/decimalToTime';
import moment from 'moment';
import PecaAiShippingAmountFlag from '../../PecaAiShippingAmountFlag';
import { usePecaAiContext } from '../../PecaAiProvider';
import {
  useOperationalPecaAiProductGroupSearch,
  useOperationalPecaAiProductsSearch,
} from '@/core/domain/operational/pecaAi/pecaAi.hooks';
import {
  OperationalPecaAiProductGroupSearchResponse,
  OperationalPecaAiProductSearchItem,
  OperationalPecaAiProductsSearchResponse,
  OperationalPecaAiSelectedSpecification,
  OperationalPecaAiSpecification,
} from '@/core/domain/operational/pecaAi/pecaAi.types';
import {
  WorkOrderPecaAiSearchProduct,
  WorkOrderPecaAiSearchProductPossibilities,
} from '@/core/domain/workorder/pecaAi/workorder-pecaAi.types';
import usePecaAiProductActions from '../usePecaAiProductActions';
import PecaAiAdvancedBasket from '../AdvancedBasket';
import './styles.less';

interface Props {
  hasBasket: boolean;
}

interface FormValues {
  amount: number;
  description: string;
}

type SelectedSpecifications = Record<string, string[]>;

export default function PecaAiAdvancedSearchForm({ hasBasket }: Props) {
  const [form] = Form.useForm<FormValues>();
  const { licensePlate, postalCode } = usePecaAiContext();

  const [productGroup, setProductGroup] =
    useState<OperationalPecaAiProductGroupSearchResponse | null>(null);
  const [selectedSpecifications, setSelectedSpecifications] =
    useState<SelectedSpecifications>({});
  const [productsSearchResult, setProductsSearchResult] =
    useState<OperationalPecaAiProductsSearchResponse | null>(null);

  const productGroupSearch = useOperationalPecaAiProductGroupSearch();
  const productsSearch = useOperationalPecaAiProductsSearch();
  const pecaAiProductActions = usePecaAiProductActions();

  const isLoading =
    productGroupSearch.isLoading ||
    productsSearch.isLoading ||
    pecaAiProductActions.isLoading;

  const productAmount = getAmount(Form.useWatch('amount', form));
  const productDescription = Form.useWatch('description', form);
  const canSearch =
    !!String(productDescription || '').trim() &&
    !!licensePlate &&
    !!postalCode &&
    !isLoading;

  function getAmount(value?: number) {
    const amount = Number(value);

    return amount > 0 ? amount : 1;
  }

  function toSelectedSpecifications(
    selected: SelectedSpecifications,
  ): OperationalPecaAiSelectedSpecification[] {
    return Object.keys(selected)
      .map((id) => ({
        id,
        values: selected[id].filter(Boolean),
      }))
      .filter((specification) => specification.values.length);
  }

  function hasPossibilities(
    product: OperationalPecaAiProductSearchItem,
  ): product is WorkOrderPecaAiSearchProduct {
    return 'possibilities' in product;
  }

  function normalizePossibility(
    possibility: WorkOrderPecaAiSearchProductPossibilities,
    fallbackDescription: string,
    amount: number,
  ): WorkOrderPecaAiSearchProductPossibilities {
    return {
      ...possibility,
      amount: possibility.amount || amount,
      description: (
        possibility.description ||
        fallbackDescription ||
        ''
      ).toLocaleUpperCase(),
      observation: possibility.observation || [],
    };
  }

  function flattenProducts(
    response: OperationalPecaAiProductsSearchResponse | null,
    amount: number,
  ) {
    if (!response?.products?.length) {
      return [];
    }

    return response.products.reduce<
      WorkOrderPecaAiSearchProductPossibilities[]
    >((acc, product) => {
      if (hasPossibilities(product)) {
        const possibilities = product.possibilities || [];

        return [
          ...acc,
          ...possibilities.map((possibility) =>
            normalizePossibility(possibility, product.description, amount),
          ),
        ];
      }

      return [
        ...acc,
        normalizePossibility(product, product.description, amount),
      ];
    }, []);
  }

  function getSpecificationOptions(
    specification: OperationalPecaAiSpecification,
    selectedValues: string[],
  ) {
    const options = [...(specification.values || [])];

    selectedValues.forEach((selectedValue) => {
      if (!options.some((option) => option.value === selectedValue)) {
        options.push({ value: selectedValue });
      }
    });

    return options.filter((option) => !!option.value);
  }

  function mergeSpecificationsWithSelected(
    specifications: OperationalPecaAiSpecification[],
    fallbackSpecifications: OperationalPecaAiSpecification[],
    selected: SelectedSpecifications,
  ) {
    const merged = [...specifications];

    Object.keys(selected).forEach((specificationId) => {
      if (
        !merged.some((specification) => specification.id === specificationId)
      ) {
        const fallback = fallbackSpecifications.find(
          (specification) => specification.id === specificationId,
        );

        merged.push(
          fallback || {
            id: specificationId,
            name: specificationId,
            values: selected[specificationId].map((value) => ({ value })),
          },
        );
      }
    });

    return merged;
  }

  const specifications = useMemo(() => {
    const currentSpecifications =
      productsSearchResult?.aggregations?.specifications ||
      productsSearchResult?.specifications ||
      productGroup?.specifications ||
      [];

    return mergeSpecificationsWithSelected(
      currentSpecifications,
      productGroup?.specifications || [],
      selectedSpecifications,
    );
  }, [
    productGroup?.specifications,
    productsSearchResult,
    selectedSpecifications,
  ]);

  const products = useMemo(
    () => flattenProducts(productsSearchResult, productAmount),
    [productAmount, productsSearchResult],
  );

  const selectedTags = useMemo(() => {
    return toSelectedSpecifications(selectedSpecifications).flatMap(
      (selectedSpecification) => {
        const specification = specifications.find(
          (item) => item.id === selectedSpecification.id,
        );

        return selectedSpecification.values.map((value) => ({
          id: selectedSpecification.id,
          name: specification?.name || selectedSpecification.id,
          value,
        }));
      },
    );
  }, [selectedSpecifications, specifications]);

  const searchProductsBySpecifications = useCallback(
    async (
      productGroupId: string,
      selected: SelectedSpecifications,
      amount: number,
    ) => {
      if (!licensePlate || !postalCode) {
        return;
      }

      const result = await productsSearch.mutateAsync({
        plate: licensePlate,
        postalCode,
        products: [
          {
            description: '',
            productGroups: [productGroupId],
            specifications: toSelectedSpecifications(selected),
            amount,
          },
        ],
      });

      setProductsSearchResult(result);
    },
    [licensePlate, postalCode, productsSearch],
  );

  const handleProductGroupSearch = async (values: FormValues) => {
    const productGroupDescription = values.description?.trim();

    if (!productGroupDescription) {
      return;
    }

    const result = await productGroupSearch.mutateAsync({
      productGroup: productGroupDescription,
      specifications: [],
    });

    setProductGroup(result);
    setSelectedSpecifications({});
    setProductsSearchResult(null);

    if (result?.productGroupId) {
      await searchProductsBySpecifications(
        result.productGroupId,
        {},
        getAmount(values.amount),
      );
    }
  };

  const handleSpecificationChange = async (
    specificationId: string,
    values: string[],
  ) => {
    if (!productGroup?.productGroupId) {
      return;
    }

    const nextSelected = {
      ...selectedSpecifications,
      [specificationId]: values,
    };

    if (!values.length) {
      delete nextSelected[specificationId];
    }

    setSelectedSpecifications(nextSelected);

    await searchProductsBySpecifications(
      productGroup.productGroupId,
      nextSelected,
      productAmount,
    );
  };

  const handleRemoveSpecification = async (
    specificationId: string,
    value: string,
  ) => {
    const values = (selectedSpecifications[specificationId] || []).filter(
      (item) => item !== value,
    );

    await handleSpecificationChange(specificationId, values);
  };

  const handleClearFilters = async () => {
    if (!productGroup?.productGroupId) {
      return;
    }

    setSelectedSpecifications({});
    await searchProductsBySpecifications(
      productGroup.productGroupId,
      {},
      productAmount,
    );
  };

  const renderExpectedDelivery = (
    product: WorkOrderPecaAiSearchProductPossibilities,
  ) => {
    if (!product.expectedDelivery) {
      return decimalToTime(product.deadlineHours);
    }

    const delivery = moment(product.expectedDelivery);

    return (
      <Tooltip title={delivery.format('DD/MM/YYYY HH:mm')}>
        <Typography.Text>{delivery.format('DD/MM HH:mm')}</Typography.Text>
      </Tooltip>
    );
  };

  const columns: TableColumnType<WorkOrderPecaAiSearchProductPossibilities>[] =
    [
      {
        title: 'Produto',
        width: 360,
        render: (_, item) => (
          <Space size={10} align="start" className="peca-ai-advanced-product">
            <Avatar
              shape="square"
              size={44}
              src={item.image}
              icon={<Package size={18} />}
              className="peca-ai-advanced-product__image"
            />

            <div>
              <Typography.Text
                strong
                className="peca-ai-advanced-product__name"
              >
                {item.description}
              </Typography.Text>

              {!!item.observation?.length && (
                <Typography.Paragraph className="peca-ai-advanced-product__observation">
                  {item.observation[0].description}
                </Typography.Paragraph>
              )}
            </div>
          </Space>
        ),
      },
      { title: 'Cód. peça', dataIndex: 'partNumber' },
      { title: 'Marca', dataIndex: 'brand' },
      {
        title: 'Frete',
        align: 'right',
        render: (_, item) => (
          <PecaAiShippingAmountFlag value={item.shippingPrice} />
        ),
      },
      {
        title: 'Preço',
        align: 'right',
        render: (_, item) => (
          <Typography.Text strong>
            {formatterPrice(item.productUnitPrice)}
          </Typography.Text>
        ),
      },
      {
        title: 'Entrega',
        align: 'center',
        width: 120,
        render: (_, item) => renderExpectedDelivery(item),
      },
      {
        align: 'right',
        width: 64,
        render: (_, item) => (
          <Tooltip title="Adicionar à cesta">
            <Button
              shape="circle"
              type="primary"
              icon={<Check size={16} />}
              loading={pecaAiProductActions.isLoading}
              onClick={() => pecaAiProductActions.addPossibility(item)}
              className="peca-ai-advanced-search__add-button"
            />
          </Tooltip>
        ),
      },
    ];

  return (
    <div className="peca-ai-advanced-search">
      <Form
        form={form}
        initialValues={{ amount: 1, description: '' }}
        onFinish={handleProductGroupSearch}
        className="peca-ai-advanced-search__form"
      >
        <Row gutter={[12, 12]} align="middle">
          <Col flex="150px">
            <FloatingFormItem name="amount" label="Quantidade">
              <InputDecimal min={0} disabled={isLoading} />
            </FloatingFormItem>
          </Col>

          <Col flex="auto">
            <FloatingFormItem name="description" label="Descrição do produto">
              <Input disabled={isLoading} allowClear />
            </FloatingFormItem>
          </Col>

          <Col>
            <Button
              type="primary"
              htmlType="submit"
              loading={productGroupSearch.isLoading}
              disabled={!canSearch}
              icon={<Search size={16} />}
              className="peca-ai-advanced-search__search-button"
            >
              Buscar
            </Button>
          </Col>
        </Row>
      </Form>

      {!!productGroup && (
        <Row
          align="middle"
          justify="space-between"
          className="peca-ai-advanced-search__summary"
        >
          <Group gap={5}>
            <Info size={16} />

            <Typography.Text>
              Grupo encontrado:{' '}
              <Typography.Text strong>
                {productGroup.productGroup}
              </Typography.Text>
            </Typography.Text>
          </Group>

          <Tag color="blue">
            {`${products.length} produto${products.length > 1 ? 's' : ''}`}
          </Tag>
        </Row>
      )}

      <div className="peca-ai-advanced-search__commerce-layout">
        {!!productGroup && (
          <>
            <div className="peca-ai-advanced-search__filters-panel">
              <Row
                align="middle"
                justify="space-between"
                className="peca-ai-advanced-search__section-title"
              >
                <Group gap={5}>
                  <Filter size={18} />
                  <Typography.Text strong>Filtros</Typography.Text>
                </Group>

                {!!selectedTags.length && (
                  <Button
                    type="link"
                    size="small"
                    onClick={handleClearFilters}
                    disabled={productsSearch.isLoading}
                  >
                    <Group gap={0}>
                      <X size={14} />
                      Limpar
                    </Group>
                  </Button>
                )}
              </Row>

              {!!selectedTags.length && (
                <div className="peca-ai-advanced-search__selected-tags">
                  {selectedTags.map((tag) => (
                    <Tag
                      key={`${tag.id}-${tag.value}`}
                      closable
                      onClose={(event) => {
                        event.preventDefault();
                        handleRemoveSpecification(tag.id, tag.value);
                      }}
                    >
                      {tag.name}: {tag.value}
                    </Tag>
                  ))}
                </div>
              )}

              <Collapse ghost>
                {specifications.map((specification) => {
                  const selectedCount =
                    selectedSpecifications[specification.id]?.length || 0;

                  return (
                    <Collapse.Panel
                      header={
                        <Row
                          align="middle"
                          justify="space-between"
                          style={{ width: '100%' }}
                        >
                          <Typography.Text>
                            {specification.name}
                          </Typography.Text>

                          {!!selectedCount && (
                            <Tag color="blue">{selectedCount}</Tag>
                          )}
                        </Row>
                      }
                      key={specification.id}
                    >
                      <Checkbox.Group
                        value={selectedSpecifications[specification.id] || []}
                        onChange={(values) =>
                          handleSpecificationChange(
                            specification.id,
                            values.map((value) => String(value)),
                          )
                        }
                        disabled={productsSearch.isLoading}
                      >
                        <Space
                          direction="vertical"
                          className="peca-ai-advanced-search__filter-options"
                        >
                          {getSpecificationOptions(
                            specification,
                            selectedSpecifications[specification.id] || [],
                          ).map((option) => (
                            <Checkbox
                              key={option.value}
                              value={option.value}
                              className="peca-ai-advanced-search__filter-option"
                            >
                              <Typography.Text>{option.value}</Typography.Text>

                              {typeof option.hits === 'number' && (
                                <Typography.Text type="secondary">
                                  {option.hits}
                                </Typography.Text>
                              )}
                            </Checkbox>
                          ))}
                        </Space>
                      </Checkbox.Group>
                    </Collapse.Panel>
                  );
                })}
              </Collapse>
            </div>

            <div className="peca-ai-advanced-search__results-panel">
              <Row
                align="middle"
                justify="space-between"
                className="peca-ai-advanced-search__section-title"
              >
                <Typography.Text strong>Produtos encontrados</Typography.Text>

                <Typography.Text type="secondary">
                  {`${products.length} resultado${
                    products.length > 1 ? 's' : ''
                  }`}
                </Typography.Text>
              </Row>

              <Table
                rowKey="id"
                size="middle"
                loading={
                  productsSearch.isLoading || pecaAiProductActions.isLoading
                }
                dataSource={products}
                columns={columns}
                scroll={{ x: true }}
                pagination={{
                  pageSize: 5,
                  showSizeChanger: false,
                }}
                locale={{
                  emptyText: (
                    <Empty description="Nenhum produto encontrado para os filtros selecionados" />
                  ),
                }}
              />
            </div>
          </>
        )}

        {!productGroup && (
          <div className="peca-ai-advanced-search__start-panel">
            <Empty description="Nenhuma busca realizada" />
          </div>
        )}

        <PecaAiAdvancedBasket hasBasket={hasBasket} />
      </div>
    </div>
  );
}
